import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Pill, Plus, Search, CheckCircle2, Clock, AlertTriangle,
  RefreshCw, ChevronRight, Stethoscope, Heart, Activity,
  Package, Wand2, Save, X
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

// Predefined order sets
const ORDER_SETS = [
  {
    id: 'comfort_care',
    name: 'Comfort Care Protocol',
    description: 'Standard comfort care medication orders',
    orders: [
      { name: 'Morphine Sulfate', dose: '5-10mg', route: 'PO/SL', frequency: 'Q4H PRN', indication: 'Pain' },
      { name: 'Lorazepam', dose: '0.5-1mg', route: 'PO/SL', frequency: 'Q4H PRN', indication: 'Anxiety' },
      { name: 'Haloperidol', dose: '0.5-2mg', route: 'PO/SL', frequency: 'Q6H PRN', indication: 'Agitation/Delirium' },
      { name: 'Glycopyrrolate', dose: '0.2mg', route: 'SL', frequency: 'Q4H PRN', indication: 'Secretions' }
    ]
  },
  {
    id: 'dyspnea',
    name: 'Dyspnea Management',
    description: 'Orders for respiratory distress',
    orders: [
      { name: 'Morphine Sulfate', dose: '2.5-5mg', route: 'PO/SL', frequency: 'Q2H PRN', indication: 'Dyspnea' },
      { name: 'Lorazepam', dose: '0.5mg', route: 'SL', frequency: 'Q4H PRN', indication: 'Anxiety with dyspnea' },
      { name: 'Oxygen', dose: '2-4L', route: 'NC', frequency: 'PRN', indication: 'O2 sat < 90%' },
      { name: 'Nebulizer Treatment', dose: 'Albuterol 2.5mg', route: 'INH', frequency: 'Q4H PRN', indication: 'Wheezing' }
    ]
  },
  {
    id: 'nausea',
    name: 'Nausea & Vomiting',
    description: 'Anti-emetic protocol',
    orders: [
      { name: 'Ondansetron', dose: '4mg', route: 'PO/SL', frequency: 'Q6H PRN', indication: 'Nausea' },
      { name: 'Prochlorperazine', dose: '10mg', route: 'PO', frequency: 'Q6H PRN', indication: 'Nausea' },
      { name: 'Haloperidol', dose: '0.5mg', route: 'PO', frequency: 'Q8H PRN', indication: 'Refractory nausea' }
    ]
  },
  {
    id: 'pain_crisis',
    name: 'Pain Crisis Protocol',
    description: 'For uncontrolled breakthrough pain',
    orders: [
      { name: 'Morphine Sulfate', dose: '10-20mg', route: 'PO/SL', frequency: 'Q2H PRN', indication: 'Severe pain' },
      { name: 'Morphine Sulfate IR', dose: '15mg', route: 'PO', frequency: 'Q4H ATC', indication: 'Baseline pain' },
      { name: 'Dexamethasone', dose: '4mg', route: 'PO', frequency: 'BID', indication: 'Inflammation' },
      { name: 'Lidocaine Patch', dose: '5%', route: 'TOP', frequency: '12 on/12 off', indication: 'Localized pain' }
    ]
  },
  {
    id: 'terminal',
    name: 'Active Dying Protocol',
    description: 'Orders for imminent death',
    orders: [
      { name: 'Morphine Sulfate Concentrate', dose: '5-20mg', route: 'SL', frequency: 'Q1H PRN', indication: 'Pain/Dyspnea' },
      { name: 'Lorazepam', dose: '1-2mg', route: 'SL', frequency: 'Q1H PRN', indication: 'Anxiety/Restlessness' },
      { name: 'Haloperidol', dose: '2mg', route: 'SL', frequency: 'Q4H PRN', indication: 'Terminal agitation' },
      { name: 'Atropine 1%', dose: '2 drops', route: 'SL', frequency: 'Q4H PRN', indication: 'Secretions' },
      { name: 'Glycopyrrolate', dose: '0.4mg', route: 'SL', frequency: 'Q4H PRN', indication: 'Secretions' }
    ]
  },
  {
    id: 'constipation',
    name: 'Bowel Protocol',
    description: 'For opioid-induced constipation',
    orders: [
      { name: 'Senna-Docusate', dose: '2 tabs', route: 'PO', frequency: 'BID', indication: 'Prevention' },
      { name: 'Lactulose', dose: '15-30mL', route: 'PO', frequency: 'BID PRN', indication: 'Constipation' },
      { name: 'Bisacodyl', dose: '10mg', route: 'PR', frequency: 'Daily PRN', indication: 'No BM x3 days' },
      { name: 'Fleet Enema', dose: '1 unit', route: 'PR', frequency: 'PRN', indication: 'Impaction' }
    ]
  }
];

const SmartOrderSet = ({ patientId, onClose }) => {
  const [selectedSet, setSelectedSet] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [customizations, setCustomizations] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const token = localStorage.getItem('token');

  // Filter order sets
  const filteredSets = ORDER_SETS.filter(set => 
    set.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    set.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Select an order set
  const selectOrderSet = (set) => {
    setSelectedSet(set);
    // Pre-select all orders
    const orders = {};
    set.orders.forEach((order, i) => {
      orders[i] = true;
    });
    setSelectedOrders(orders);
  };

  // Toggle order selection
  const toggleOrder = (index) => {
    setSelectedOrders(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Submit orders
  const submitOrders = async () => {
    if (!selectedSet) return;

    const ordersToSubmit = selectedSet.orders
      .filter((_, i) => selectedOrders[i])
      .map((order, i) => ({
        ...order,
        ...customizations[i]
      }));

    if (ordersToSubmit.length === 0) {
      toast({ title: "No Orders Selected", description: "Please select at least one order", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/phase45/orders/smart-set`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patient_id: patientId,
          order_set_id: selectedSet.id,
          orders: ordersToSubmit
        })
      });

      if (res.ok) {
        toast({
          title: "Orders Submitted",
          description: `${ordersToSubmit.length} orders from ${selectedSet.name} submitted for physician review.`
        });
        if (onClose) onClose();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to submit orders", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="h-7 w-7 text-teal-600" />
            Smart Order Sets
          </h1>
          <p className="text-gray-600">Pre-configured medication protocols for common hospice scenarios</p>
        </div>
        {onClose && (
          <Button variant="ghost" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search order sets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Set Selection */}
        <div className="space-y-4">
          <h2 className="font-semibold text-gray-700">Available Order Sets</h2>
          {filteredSets.map((set) => (
            <Card 
              key={set.id}
              className={`cursor-pointer transition-all ${
                selectedSet?.id === set.id 
                  ? 'border-teal-500 bg-teal-50' 
                  : 'hover:border-gray-300'
              }`}
              onClick={() => selectOrderSet(set)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      set.id === 'comfort_care' ? 'bg-teal-100' :
                      set.id === 'dyspnea' ? 'bg-blue-100' :
                      set.id === 'nausea' ? 'bg-amber-100' :
                      set.id === 'pain_crisis' ? 'bg-red-100' :
                      set.id === 'terminal' ? 'bg-purple-100' : 'bg-green-100'
                    }`}>
                      <Pill className={`h-5 w-5 ${
                        set.id === 'comfort_care' ? 'text-teal-600' :
                        set.id === 'dyspnea' ? 'text-blue-600' :
                        set.id === 'nausea' ? 'text-amber-600' :
                        set.id === 'pain_crisis' ? 'text-red-600' :
                        set.id === 'terminal' ? 'text-purple-600' : 'text-green-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{set.name}</h3>
                      <p className="text-sm text-gray-600">{set.description}</p>
                      <Badge variant="outline" className="mt-2">
                        {set.orders.length} orders
                      </Badge>
                    </div>
                  </div>
                  {selectedSet?.id === set.id && (
                    <CheckCircle2 className="h-5 w-5 text-teal-600" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Order Details */}
        <div className="space-y-4">
          {selectedSet ? (
            <>
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-700">{selectedSet.name} Orders</h2>
                <Badge>{Object.values(selectedOrders).filter(Boolean).length} selected</Badge>
              </div>

              <Card>
                <CardContent className="p-4 space-y-4">
                  {selectedSet.orders.map((order, i) => (
                    <div 
                      key={i}
                      className={`p-4 border rounded-lg transition-colors ${
                        selectedOrders[i] ? 'border-teal-300 bg-teal-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedOrders[i] || false}
                          onCheckedChange={() => toggleOrder(i)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{order.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {order.indication}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-2 mt-2 text-sm text-gray-600">
                            <div>
                              <span className="text-xs text-gray-400">Dose:</span>
                              <p>{customizations[i]?.dose || order.dose}</p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">Route:</span>
                              <p>{order.route}</p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">Frequency:</span>
                              <p>{order.frequency}</p>
                            </div>
                          </div>
                          {selectedOrders[i] && (
                            <div className="mt-3">
                              <Input
                                placeholder="Special instructions (optional)"
                                value={customizations[i]?.instructions || ''}
                                onChange={(e) => setCustomizations(prev => ({
                                  ...prev,
                                  [i]: { ...prev[i], instructions: e.target.value }
                                }))}
                                className="text-sm"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* AI Suggestions */}
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Wand2 className="h-5 w-5 text-purple-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-purple-900">AI Recommendation</h4>
                      <p className="text-sm text-purple-700 mt-1">
                        Based on the patient's current symptoms, consider adding PRN Lorazepam for anxiety 
                        if not already included in the order set.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Button 
                className="w-full bg-teal-600 hover:bg-teal-700"
                onClick={submitOrders}
                disabled={loading || Object.values(selectedOrders).filter(Boolean).length === 0}
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Submit {Object.values(selectedOrders).filter(Boolean).length} Orders for Review
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                Orders will be sent to the hospice physician for co-signature
              </p>
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Package className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-600">Select an Order Set</h3>
                <p className="text-sm text-gray-500">
                  Choose a pre-configured order set from the list to view and customize orders
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartOrderSet;
